import { useEffect, useState } from "react";
import { api, currencyFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/StatCard";
import {
  ReceiptText, Clock, CheckCircle2, XCircle, Building2, Users as UsersIcon, DollarSign,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const PIE = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#F97316", "#6B7280"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;

  const greeting = { super_admin: "Global overview", admin: "Tenant overview", manager: "Approvals overview", employee: "Your claims" }[user.role];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">{greeting} · {user.name}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {user.role === "super_admin" && (
          <>
            <StatCard testId="stat-tenants" label="Tenants" value={stats.tenant_count} Icon={Building2} />
            <StatCard testId="stat-users" label="Users" value={stats.user_count} Icon={UsersIcon} />
          </>
        )}
        {user.role === "admin" && (
          <StatCard testId="stat-users" label="Team Members" value={stats.user_count} Icon={UsersIcon} />
        )}
        {user.role !== "super_admin" && (
          <>
            <StatCard testId="stat-total" label="Total Claims" value={stats.total_claims} Icon={ReceiptText} />
            <StatCard testId="stat-pending" label="Pending" value={stats.pending} accent="text-warning" Icon={Clock}
              sub={currencyFmt(stats.pending_amount)} />
            <StatCard testId="stat-approved" label="Approved" value={stats.approved} accent="text-success" Icon={CheckCircle2}
              sub={currencyFmt(stats.approved_amount)} />
            <StatCard testId="stat-rejected" label="Rejected" value={stats.rejected} accent="text-danger" Icon={XCircle} />
            <StatCard testId="stat-approved-amount" label="Approved Spend" value={currencyFmt(stats.approved_amount)} Icon={DollarSign} />
          </>
        )}
      </div>

      {user.role !== "super_admin" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-4">Approved Spend by Month</h3>
          {stats.trend?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => currencyFmt(v)} />
                <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </div>

        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-4">Spend by Category</h3>
          {stats.by_category?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stats.by_category} dataKey="value" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {stats.by_category.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(v, n) => [currencyFmt(v), n]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
          <div className="mt-3 space-y-1.5">
            {stats.by_category?.slice(0, 5).map((c, i) => (
              <div key={c.category} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />{c.category}</span>
                <span className="font-mono">{currencyFmt(c.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

const Empty = () => (
  <div className="h-[280px] grid place-items-center text-sm text-muted-foreground">No approved data yet.</div>
);
